import { useState } from "react";
import { Link } from "react-router-dom";
import { resetPassword } from "../lib/dataStore";
import { toast } from "sonner";
import { KeyRound, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Please enter your email address");
    setBusy(true);
    try {
      await resetPassword(email);
      setSent(true);
      toast.success("Password reset instructions sent!");
    } catch (err) {
      toast.error(err.message || "Failed to send reset email");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-6 bg-muted/20">
      <div className="w-full max-w-md bg-card border border-border p-8 md:p-10 shadow-sm rounded-lg">
        <Link
          data-testid="forgot-back-to-login"
          to="/login"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
        </Link>

        <div className="mb-6">
          <div className="w-12 h-12 rounded-full bg-terracotta/10 text-terracotta flex items-center justify-center mb-4">
            <KeyRound className="w-6 h-6" />
          </div>
          <div className="overline text-terracotta mb-1">Account Recovery</div>
          <h1 className="font-serif text-3xl">Reset your password</h1>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            Enter the email associated with your Lineacraft account and we will send you a password reset link.
          </p>
        </div>

        {sent ? (
          <div className="space-y-6 text-center py-4">
            <div className="flex justify-center text-emerald-600">
              <CheckCircle2 className="w-12 h-12" />
            </div>
            <div>
              <h2 className="font-serif text-xl">Check your inbox</h2>
              <p className="text-sm text-muted-foreground mt-2">
                We sent password reset instructions to <strong>{email}</strong>.
              </p>
            </div>
            <div className="pt-2">
              <button
                type="button"
                onClick={() => { setSent(false); setEmail(""); }}
                className="text-xs text-terracotta underline underline-offset-4"
              >
                Try a different email address
              </button>
            </div>
            <Link
              to="/login"
              className="w-full py-3 rounded-full bg-foreground text-background hover:bg-terracotta block font-medium text-sm transition"
            >
              Return to Sign In
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <label className="block">
              <span className="block text-sm mb-1.5 font-medium">Email address</span>
              <input
                data-testid="forgot-password-email"
                required
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-muted/50 border border-border px-4 py-3 rounded-md outline-none focus:border-foreground text-sm transition"
              />
            </label>

            <button
              data-testid="forgot-password-submit"
              disabled={busy}
              className="w-full py-3 rounded-full bg-terracotta text-primary-foreground hover:opacity-90 disabled:opacity-50 font-medium text-sm transition"
            >
              {busy ? "Sending link…" : "Send Reset Link"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
